// Team color helpers shared by the pick cards and Team Management.
//
// Team colors are typed by hand in Team Management and used directly as CSS
// values. A hex code saved without its "#" (Texas's font color was stored as
// "bf5700") is invalid CSS, so the browser silently drops it and the card's
// name, rank, record, and conference fall back to an invisible default. These
// helpers repair that one common typo and reject anything else a browser
// wouldn't render.

// A bare hex code: 3, 4, 6, or 8 hex digits with no leading "#".
const BARE_HEX = /^(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/**
 * Trim a stored color and add the "#" a bare hex code is missing.
 * Everything else (named colors, "#abc123", rgb()) passes through trimmed.
 * @param {unknown} value
 * @returns {string} '' for a missing value
 */
export const normalizeColor = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return BARE_HEX.test(trimmed) ? `#${trimmed}` : trimmed;
};

/**
 * Whether a (normalized) color is something the browser will render.
 * Blank counts as valid: the field is simply unset.
 * Uses the browser's own CSS parser when available; falls back to hex or a
 * plain color keyword where it isn't (tests, very old browsers).
 * @param {string} value
 * @returns {boolean}
 */
export const isValidColor = (value) => {
  if (value === '') return true;
  if (typeof value !== 'string') return false;
  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
    return CSS.supports('color', value);
  }
  return HEX.test(value) || /^[a-z]+$/i.test(value);
};

/**
 * Copy of a team with both colors normalized, for rendering.
 * @template T
 * @param {T} team
 * @returns {T}
 */
export const withNormalizedColors = (team) => (
  team ? { ...team, color: normalizeColor(team.color), fontColor: normalizeColor(team.fontColor) } : team
);
